// Copyright (c) 2022-2023. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { AuthSessionData } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import { useMyClientList } from "../../../../hooks/useMyClientList";
import { MySelectClientArticle } from "../selectClient/MySelectClientArticle";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { ClientDTO } from "../../../../../../../io/hyperify/core/store/types/client/ClientDTO";
import { MyOrderConfirmSelectedClientArticle } from "./MyOrderConfirmSelectedClientArticle";
import { useCallback, useState } from "react";
import { OrderDTO } from "../../../../../../../io/hyperify/core/store/types/order/OrderDTO";
import { MyOrderCompleteArticle } from "./MyOrderCompleteArticle";
import { MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import {
    T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_COMPANY,
    T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_PERSON,
    T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_ANY_CLIENT_LABEL,
    T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_OTHER,
    T_MY_ORDER_VIEW_SELECT_CLIENT_TITLE
} from "../../../../../core/constants/translation";
import { StoreCartService } from "../../../../../../../io/hyperify/core/StoreCartService";
import { RouteService } from "../../../../../../../io/hyperify/frontend/services/RouteService";
import { GET_MY_CLIENT_INVOICE_ROUTE } from "../../../../../core/constants/route";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import "./MyOrderAuthorizedArticle.scss";

const LOG = LogService.createLogger('MyOrderAuthorizedArticle');

export interface MyOrderAuthorizedArticleProps {
    readonly className?: string;
    readonly t: TranslationFunction;
    readonly session: AuthSessionData;
}

export function MyOrderAuthorizedArticle (props: MyOrderAuthorizedArticleProps) {
    const className = props?.className;
    const t = props?.t;
    const session = props?.session;
    const email = session?.email;
    const [list, refreshList] = useMyClientList(session?.token);
    const [client, setClient] = useState<ClientDTO | undefined>(undefined);
    const [order, setOrder] = useState<OrderDTO | undefined>(undefined);
    const translationParams = {
        EMAIL: email
    };

    const selectClient = useCallback(
        (client: ClientDTO) => {
            LOG.debug(`Selected client: `, client);
            refreshList();
            setClient(client);
        },
        [
            refreshList,
            setClient
        ]
    );

    const submitOrderCallback = useCallback(
        (order: OrderDTO) => {
            const clientId = order?.clientId;
            const invoiceId = order?.invoice?.invoiceId;
            StoreCartService.resetCart();
            if (clientId && invoiceId) {
                LOG.info(`Changing to invoice: `, invoiceId, clientId);
                RouteService.setRoute( GET_MY_CLIENT_INVOICE_ROUTE(clientId, invoiceId) );
            } else {
                LOG.warn(`No clientId or invoiceID found -- falling back to original functionality: `, clientId, invoiceId);
                setOrder(order);
            }
        },
        [
            setOrder
        ]
    );

    const cancelOrderCallback = useCallback(
        () => {
            setClient(undefined);
            refreshList();
        },
        [
            setClient,
            refreshList
        ]
    );

    if (order) {
        return (
            <MyOrderCompleteArticle
                t={t}
                className={`${MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
                session={session}
                order={order}
            />
        );
    }

    if (!list) {
        return (
            <Loader
                className={`${MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
            />
        );
    }

    if (client !== undefined) {
        return (
            <MyOrderConfirmSelectedClientArticle
                t={t}
                className={`${MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
                session={session}
                client={client}
                backAction={cancelOrderCallback}
                submitOrder={submitOrderCallback}
            />
        );
    }

    return (
        <MySelectClientArticle
            t={t}
            className={`${MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
            title={t(T_MY_ORDER_VIEW_SELECT_CLIENT_TITLE, translationParams)}
            session={session}
            client={client}
            list={list}
            selectClient={selectClient}
            translationParams={translationParams}
            selectAnyClientLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_ANY_CLIENT_LABEL, translationParams)}
            newCompanyButtonLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_COMPANY, translationParams)}
            newPersonButtonLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_PERSON, translationParams)}
            reLoginButtonLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_OTHER, translationParams)}
        />
    );

}
